
import { Component, Ref, Vue } from 'vue-property-decorator'
import StudentListLayout from '@/components/templates/manage/StudentListLayout.vue'
import ModalEditPassword from '@/views/modal/manage/ModalEditPassword.vue'
import ModalEditTemporaryPassword from '@/views/modal/manage/ModalEditTemporaryPassword.vue'
import { GradeCode } from '@/const/GradeCode'
import { UserStatus } from '@/const/manage/UserStatus'
import { StudentList } from '@/models/manage/StudentList'
import { SelectItem } from '@/models/SelectItem'
import DownloadUtils from '@/utils/DownloadUtils'
import { AcademyCoursesApi } from '@/api/methods/manage/AcademyCoursesApi'
import { AcademyClassesApi } from '@/api/methods/manage/AcademyClassesApi'
import { UserStudentsApi } from '@/api/methods/manage/UserStudentsApi'
import { TemporaryStudentsApi } from '@/api/methods/manage/TemporaryStudentsApi'
import { UserStudentsApi as CsvUserStudentsApi } from '@/api/methods/export/csv/UserStudentsApi'
import { TemporaryStudentsApi as CsvTemporaryStudentsApi } from '@/api/methods/export/csv/TemporaryStudentsApi'

/**
 * 生徒一覧(教室)
 */
@Component({
  components: {
    StudentListLayout,
    ModalEditPassword,
    ModalEditTemporaryPassword,
  },
})
export default class BranchStudents extends Vue {
  /** 生徒一覧 */
  @Ref()
  studentListLayout!: StudentListLayout

  /** 生徒パスワード更新モーダル */
  @Ref()
  modalEditPassword!: ModalEditPassword

  /** 仮生徒パスワード更新モーダル */
  @Ref()
  modalEditTemporaryPassword!: ModalEditTemporaryPassword

  /** 1ページの最大件数 */
  private readonly MAX_PER_PAGE = 7

  /** ページ指定の初期値 */
  private readonly DEFAULT_PAGE_NUMBER = 1

  /** プルダウン選択固定値:全て */
  private readonly OPTION_ALL = { value: 'all', label: '全て' }

  /** プルダウン選択固定値:選択不可 */
  private readonly OPTION_DISABLED = { value: 'disabled', label: '選択不可' }

  /** ユーザのアクティベート状態 */
  private isActivated = true

  /** 生徒一覧 */
  private studentList: StudentList[] = []

  /** 生徒人数 */
  private studentsNumber = 0

  /** 学年フィルターの選択肢 */
  private filterGradeList: SelectItem[] = []

  /** コースフィルターの選択肢 */
  private filterAcademyCourseList: SelectItem[] = []

  /** クラスフィルターの選択肢 */
  private filterAcademyClassList: SelectItem[] = []

  /** クラスフィルターのdisabled制御 */
  private isClassFilterDisabled = false

  /** ページ数 */
  private pageLength = 0

  /** 現在のページ */
  private currentPage = this.DEFAULT_PAGE_NUMBER

  /** 先生情報 */
  private userbranch = this.$appCookies.authTeacher.get()

  /**
   * ユーザのアクティベート状態フィルターの選択肢
   *
   * @returns フィルターの選択肢
   */
  private get filterUserActivateStatusList(): SelectItem[] {
    return [UserStatus.OPTION_ACTIVATED_USER, UserStatus.OPTION_TEMPORARY_USER]
  }

  /**
   * ユーザのアクティベート状態フィルターの初期値
   *
   * @returns 本ユーザ(アクティベート済)
   */
  private get defaultUserActivateStatus(): string {
    return UserStatus.OPTION_ACTIVATED_USER.value
  }

  /**
   * 学年フィルターの初期選択値
   *
   * @returns 選択肢「全て」のコード
   */
  private get defaultGardeCode(): string {
    return this.OPTION_ALL.value
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 学年一覧取得(学年プルダウン設定)
    this.getGrades()

    // コース一覧取得(プルダウン設定)
    await this.getAcademyCourses()

    // クラス一覧取得(プルダウン設定)
    await this.getAcademyClasses()

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }

  /**
   * 学年一覧取得(学年プルダウン設定)
   */
  private getGrades(): void {
    // 学年プルダウン設定
    this.filterGradeList = GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })

    // 選択肢[全て]を先頭に追加する
    this.filterGradeList.unshift(this.OPTION_ALL)
  }

  /**
   * コース一覧取得(コースプルダウン設定)
   */
  private async getAcademyCourses(): Promise<void> {
    // コース一覧取得のパラメータ設定
    let options = { branchId: this.userbranch.branchId }

    // 選択中の学年があれば追加
    if (this.currentGrade()) {
      Object.assign(options, { gradeCode: this.currentGrade() })
    }

    // コース一覧取得
    let academyCourses
    if (this.isActivated) {
      academyCourses = await AcademyCoursesApi.getCoursesHasStudents(options)
    } else {
      academyCourses = await AcademyCoursesApi.getCoursesHasTemporaryStudents(options)
    }

    // コースプルダウン設定
    this.filterAcademyCourseList = academyCourses.map((course) => {
      return {
        // コースIDをコードとして使用
        value: String(course.id),
        label: course.name,
      }
    })

    // コースフィルター先頭に「全て」の選択肢を追加
    this.filterAcademyCourseList.unshift(this.OPTION_ALL)

    // コースフィルターの初期選択値設定
    this.setCurrentCourseValue(this.OPTION_ALL.value)
  }

  /**
   * クラス一覧取得(クラスプルダウン設定)
   */
  private async getAcademyClasses(): Promise<void> {
    // disabledを外す
    this.isClassFilterDisabled = false

    // コースが未選択(全てのコース)の場合、固定値を設定し処理を終了(クラス選択はDisabledになる)
    if (!this.currentAcademyCourse()) {
      this.filterAcademyClassList = [this.OPTION_DISABLED]
      this.setCurrentClassValue(this.OPTION_DISABLED.value)
      this.isClassFilterDisabled = true
      return
    }

    // クラス一覧取得のパラメータ設定
    let options = { academyCourseId: Number(this.currentAcademyCourse()) }

    // 選択中の学年があれば追加
    if (this.currentGrade()) {
      Object.assign(options, { gradeCode: this.currentGrade() })
    }

    // クラス一覧取得
    let academyClasses
    if (this.isActivated) {
      academyClasses = await AcademyClassesApi.getClassesHasStudents(options)
    } else {
      academyClasses = await AcademyClassesApi.getClassesHasTemporaryStudents(options)
    }

    // クラスプルダウン設定
    this.filterAcademyClassList = academyClasses.map((academyClass) => {
      return {
        value: String(academyClass.id),
        label: academyClass.name,
      }
    })

    // クラスプルダウンの先頭に「全て」の選択肢を追加
    this.filterAcademyClassList.unshift(this.OPTION_ALL)

    // クラスフィルターの初期選択値設定
    this.setCurrentClassValue(this.OPTION_ALL.value)
  }

  /**
   * 通常生徒一覧取得
   */
  private async getStudents(): Promise<void> {
    // 生徒の選択状態をクリア
    this.clearSelectedStudents()

    // 生徒一覧取得のパラメータ設定
    let options = this.getCurrentOptions()

    const responseStudents = await UserStudentsApi.getStudents(this.currentPage, this.MAX_PER_PAGE, options)

    // 生徒数設定
    this.studentsNumber = responseStudents.totalCount

    // ページング設定
    const divideNumber = Math.floor(this.studentsNumber / this.MAX_PER_PAGE)
    this.pageLength = this.studentsNumber % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // 生徒一覧テーブルデータ設定
    this.studentList = responseStudents.students.map((student) => {
      const status = student.lockedAt ? UserStatus.LOCKED : UserStatus.UNLOCKED
      return {
        userId: student.userId,
        avatarIconCode: student.avatarIconCode,
        nickname: student.nickname,
        loginId: student.loginId,
        identifyTag: student.identifyTag,
        gradeCode: student.gradeCode,
        branchName: student.branchName,
        courseName: student.courseName,
        className: student.className,
        mailAddress: student.mailAddress,
        lockedStatus: status,
        registerType: student.registerType,
        coinCount: student.coinCount,
      }
    })
  }

  /**
   * 仮生徒一覧取得
   */
  private async getTemporaryStudents(): Promise<void> {
    // 生徒の選択状態をクリア
    this.clearSelectedStudents()

    // 生徒一覧取得のパラメータ設定
    const options = this.getCurrentOptions()

    const responseStudents = await TemporaryStudentsApi.getTemporaryStudents(
      this.currentPage,
      this.MAX_PER_PAGE,
      options
    )

    // 生徒数設定
    this.studentsNumber = responseStudents.totalCount

    // ページング設定
    const divideNumber = Math.floor(this.studentsNumber / this.MAX_PER_PAGE)
    this.pageLength = this.studentsNumber % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // 生徒一覧テーブルデータ設定
    this.studentList = responseStudents.students.map((student) => {
      return {
        userId: student.userId,
        avatarIconCode: null,
        nickname: student.nickname,
        loginId: student.loginId,
        identifyTag: student.identifyTag,
        gradeCode: student.gradeCode,
        branchName: student.branchName,
        courseName: student.courseName,
        className: student.className,
        lockedStatus: UserStatus.TEMPORARY,
        registerType: student.registerType,
        coinCount: null,
      }
    })
  }

  /**
   * 選択済生徒リストのクリア
   */
  private clearSelectedStudents(): void {
    this.studentListLayout.clearSelectedStudents()
  }

  /**
   * 現在の絞り込み条件を取得
   *
   * @returns 絞り込み条件
   */
  private getCurrentOptions(): {
    /** 学年コード */
    gradeCode?: string
    /** コースID */
    courseId?: number
    /** クラスID */
    classId?: number
    /** 検索文字列 */
    searchText?: string
  } {
    let options = {}

    // 学年指定がある場合追加
    if (this.currentGrade()) {
      Object.assign(options, { gradeCode: this.currentGrade() })
    }

    // コース指定がある場合追加
    if (this.currentAcademyCourse()) {
      Object.assign(options, { courseId: Number(this.currentAcademyCourse()) })
    }

    // クラス指定がある場合追加
    if (this.currentAcademyClass()) {
      Object.assign(options, { classId: Number(this.currentAcademyClass()) })
    }

    // 名前検索文字列がある場合追加
    if (this.currentSearchText()) {
      Object.assign(options, { searchText: this.currentSearchText() })
    }

    return options
  }

  /**
   * 選択中の学年を取得
   *
   * @returns 学年コード
   */
  private currentGrade(): string | undefined {
    // 学年未選択(全て)の場合、undefinedを返す
    if (this.studentListLayout.currentGrade() === this.OPTION_ALL.value) {
      return undefined
    }
    return this.studentListLayout.currentGrade()
  }

  /**
   * 選択中のコースを取得
   *
   * @returns コースコード(ID)
   */
  private currentAcademyCourse(): string | undefined {
    // コース未選択(全て)の場合、undefinedを返す
    if (this.studentListLayout.currentCourse() === this.OPTION_ALL.value) {
      return undefined
    }
    return this.studentListLayout.currentCourse()
  }

  /**
   * コース選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  private setCurrentCourseValue(value: string): void {
    this.studentListLayout.setCurrentCourseValue(value)
  }

  /**
   * 選択中のクラスを取得
   *
   * @returns クラスコード(ID)
   */
  private currentAcademyClass(): string | undefined {
    // クラスが選択不可の場合、undefinedを返す
    if (this.studentListLayout.currentClass() === this.OPTION_DISABLED.value) {
      return undefined
    }
    // クラス未選択(全て)の場合、undefinedを返す
    if (this.studentListLayout.currentClass() === this.OPTION_ALL.value) {
      return undefined
    }

    return this.studentListLayout.currentClass()
  }

  /**
   * クラス選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  private setCurrentClassValue(value: string): void {
    this.studentListLayout.setCurrentClassValue(value)
  }

  /**
   * 生徒名検索の文字列
   *
   * @returns 検索文字列
   */
  private currentSearchText(): string {
    return this.studentListLayout.textInputedSearch()
  }

  /**
   * ユーザのアクティベート状態フィルター変更時のイベント
   */
  private async changeUserActivateStatusFilter(): Promise<void> {
    this.$loading.start()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    // アクティベート状態を更新
    this.isActivated = !this.isActivated

    // コース一覧取得(コースプルダウン設定)
    await this.getAcademyCourses()

    // クラス一覧取得(クラスプルダウン設定)
    await this.getAcademyClasses()

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }

  /**
   * 学年フィルター変更時のイベント
   */
  private async changeGradeFilter(): Promise<void> {
    this.$loading.start()

    // コース一覧取得(コースプルダウン設定)
    await this.getAcademyCourses()

    // クラス一覧取得(クラスプルダウン設定)
    await this.getAcademyClasses()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }

  /**
   * コースフィルター変更時のイベント
   */
  private async changeCourseFilter(): Promise<void> {
    this.$loading.start()

    // クラス一覧取得(クラスプルダウン設定)
    await this.getAcademyClasses()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }

  /**
   * クラスフィルター変更時のイベント
   */
  private async changeClassFilter(): Promise<void> {
    this.$loading.start()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }

  /**
   * 生徒検索
   */
  private async searchStudent(): Promise<void> {
    this.$loading.start()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }

  /**
   * 生徒一覧ダウンロード
   */
  private async downloadStudentList(): Promise<void> {
    this.$loading.start()

    // 通常ユーザ
    if (this.isActivated) {
      await this.downloadStudents()
      this.$loading.complete()
      return
    }

    // 仮ユーザ
    await this.downloadTemporaryStudents()

    this.$loading.complete()
  }

  /**
   * 通常生徒のダウンロード
   */
  private async downloadStudents(): Promise<void> {
    // 全件チェックの状態を取得
    const isCheckedAll = this.studentListLayout.studentListPanel.studentListTable.isCheckedAll

    // 全件チェック済
    if (isCheckedAll) {
      // 条件指定で全件ダウンロード実行
      await this.getStudentsCsvAll()
      return
    }

    // チェック済の生徒のユーザID取得
    const targetStudentIds = this.studentListLayout.studentListPanel.studentListTable.checkedStudentList

    // IDが空の場合
    if (targetStudentIds.length === 0) {
      alert('生徒が選択されていません')
      return
    }

    // ID指定でダウンロード実行
    await this.getStudentsCsv(targetStudentIds)
  }

  /**
   * 仮生徒のダウンロード
   */
  private async downloadTemporaryStudents(): Promise<void> {
    // 全件チェックの状態を取得
    const isCheckedAll = this.studentListLayout.studentListPanel.studentListTable.isCheckedAll

    // 全件チェック済
    if (isCheckedAll) {
      // 条件指定で全件ダウンロード実行
      await this.getTemporaryStudentsCsvAll()
      return
    }

    // チェック済の生徒のユーザID取得
    const targetStudentIds = this.studentListLayout.studentListPanel.studentListTable.checkedStudentList

    // IDが空の場合
    if (targetStudentIds.length === 0) {
      alert('生徒が選択されていません')
      return
    }

    // ID指定でダウンロード実行
    await this.getTemporaryStudentsCsv(targetStudentIds)
  }

  /**
   * 通常生徒一覧のCSVダウンロード(id指定)
   *
   * @param targetStudentIds ダウンロード対象のユーザID(配列型)
   */
  private async getStudentsCsv(targetStudentIds: number[]): Promise<void> {
    // 生徒一覧のCSV取得
    const responseStudentsCsv = await CsvUserStudentsApi.getStudentsCsv(targetStudentIds)

    // ダウンロード処理
    DownloadUtils.csv(responseStudentsCsv, DownloadUtils.fileNameStudentsCsv())
  }

  /**
   * 通常生徒一覧のCSVダウンロード(条件指定)
   */
  private async getStudentsCsvAll(): Promise<void> {
    // 生徒一覧取得のパラメータ設定
    let options = this.getCurrentOptions()

    // 生徒一覧のCSV取得
    const responseStudentsCsv = await CsvUserStudentsApi.getStudentsCsvAll(options)

    // ダウンロード処理
    DownloadUtils.csv(responseStudentsCsv, DownloadUtils.fileNameStudentsCsv())
  }

  /**
   * 仮生徒一覧のCSVダウンロード(id指定)
   *
   * @param targetStudentIds ダウンロード対象のユーザID(配列型)
   */
  private async getTemporaryStudentsCsv(targetStudentIds: number[]): Promise<void> {
    // 生徒一覧のCSV取得
    const responseStudentsCsv = await CsvTemporaryStudentsApi.getStudentsCsv(targetStudentIds)

    // ダウンロード処理
    DownloadUtils.csv(responseStudentsCsv, DownloadUtils.fileNameTemporaryStudentsCsv())
  }

  /**
   * 仮生徒一覧のCSVダウンロード(条件指定)
   */
  private async getTemporaryStudentsCsvAll(): Promise<void> {
    // 生徒一覧取得のパラメータ設定
    let options = this.getCurrentOptions()

    // 生徒一覧のCSV取得
    const responseStudentsCsv = await CsvTemporaryStudentsApi.getStudentsCsvAll(options)

    // ダウンロード処理
    DownloadUtils.csv(responseStudentsCsv, DownloadUtils.fileNameTemporaryStudentsCsv())
  }

  /**
   * パスワード編集ボタン押下時のイベント
   *
   * @param userId ユーザID
   */
  private eventClickEditPasswordButton(userId: number): void {
    // 通常ユーザの場合
    if (this.isActivated) {
      this.modalEditPassword.show(userId)
      return
    }

    // 仮ユーザの場合
    this.modalEditTemporaryPassword.show(userId)
  }

  /**
   * ページリンク押下時の処理
   *
   * @param value ページ番号
   */
  private async changePage(value: number): Promise<void> {
    this.$loading.start()

    // 現在のページを更新
    this.currentPage = value

    if (this.isActivated) {
      // 生徒一覧取得
      await this.getStudents()
    } else {
      // 仮生徒一覧取得
      await this.getTemporaryStudents()
    }

    this.$loading.complete()
  }
}
